// Copyright (c) StrangeBee 2024

import React, { ReactElement, useContext, useEffect, useMemo, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { classList, convertToLocaleId, useLocalMessage } from '@Helpers/';
import {
    ObjectTypeV1 as ObjectType,
    CaseSimilarAlertOutput,
    AlertOutput,
    QueryParams,
    CustomStatusMapType,
    AlertStatusStage,
    PermissionsEnum,
    CaseOutput,
} from 'thehive-sdk';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import ListProvider from '@Context/ListProvider';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import client from '@Client';
import { ICONS } from '@Constants/iconsApp';
import { AlertSimilarCasesStats } from '@Organisms/Alert/AlertSimilarCasesStats/AlertSimilarCasesStats';
import { FormatDate } from '@Molecules/Format';
import useConfigList from '@Hooks/useConfigList';
import { THLayout } from '@Molecules/THLayout';
import { PaginationSize } from '@Atoms/Inputs/PaginationSize/PaginationSize';
import { ManageSwitch } from '@Molecules/ManageSwitch/ManageSwitch';
import { THFilter } from '@Molecules/THFilter/THFilter';
import { THList } from '@Molecules/THList';
import { useListAllFields } from '@Queries/describeQueries';
import { Footer } from '@Organisms/ExtendFilterFooter/ExtendFilterFooter';
import { PaginationBrowse } from '@Atoms/Inputs/PaginationBrowse/PaginationBrowse';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { THInputNumber } from '@Molecules/Inputs/THInputNumber/THInputNumber';
import { AlertListTitle } from '@Organisms/Alert/AlertListTitle/AlertListTitle';
import { ColorLabel } from '@Atoms/Info/ColorLabel/ColorLabel';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { THTagSelector } from '@Molecules/Inputs/THTagSelector/THTagSelector';
import THTag from '@Atoms/THTag/THTag';
import { ManageViewList } from '@Molecules/ManageViewList/ManageViewList';
import { useDrawer } from '@Hooks/useDrawer';
import { AlertDrawer } from '@Organisms/Alert/AlertDrawer/AlertDrawer';
import { HTMLColors } from '@Constants/colors';
import { ScopeContext } from '@Context/ScopeProvider';
import IconCount from '@Atoms/IconCount/IconCount';
import { useQuery } from 'react-query';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';
import { Badge } from 'antd';
import { computeActionList } from '@Organisms/Alert/actionList';
import { computeEventList } from '@Organisms/Alert/eventList';
import { useSplitPermissions } from '@Hooks/useSplitPermissions';
import useOneQuery from '@Hooks/useOneQuery';
import { computeGetSelector } from '@Organisms/Case/selector';
import { useCurrentUser } from '@Queries/userQueries';
import ActionList from '@Molecules/ActionList/ActionList';
import usePageTitle from '@Hooks/usePageTitle';

import './SimilarAlertsList.scss';

interface SimilarStateFilter {
    title: string;
    obsPercentage: number;
    iocPercentage: number;
    dataTypes: string[];
}

const stateDefaultValue: SimilarStateFilter = {
    dataTypes: [],
    iocPercentage: 0,
    obsPercentage: 0,
    title: '',
};

const SimilarAlertsList = (): ReactElement => {
    const i = useLocalMessage();
    const history = useHistory();

    const { caseId } = useParams<{ caseId: string }>();

    const { data: describe } = useListAllFields();

    const {
        customStatus: {
            alert: { getStatus },
        },
    } = useContext(ScopeContext);

    const { data: currentUserData } = useCurrentUser();

    const { visible, setVisible, data, setData } = useDrawer<AlertOutput>({});

    const config = useConfigList<CaseSimilarAlertOutput>({
        selector: {
            head: [
                {
                    name: 'getCase',
                    idOrName: caseId,
                },
                {
                    name: 'similarAlerts',
                    alertFilter: {},
                },
            ],
            name: 'get-case-similar-alerts',
        },
        selectorCustomCount: {
            head: [
                {
                    name: 'getCase',
                    idOrName: caseId,
                },
                {
                    name: 'similarAlertCount',
                },
            ],
            name: 'count-get-case-similar-alerts',
        },
        controller: client.AlertController,
        storageId: StorageFilterEnum.CASE_SIMILAR_ALERTS,
        objectType: ObjectType.ALERT,
        pagination: 'manual',
    });

    const {
        dataSource = [],
        columnsRenderer,
        storagePageManager,
        storageViewManager,
        status = 'loading',
        listRefetch,
        countRefetch,
        stateNeedRefresh: { needRefresh, setNeedRefresh },
        sortManager: { getFieldSort },
        paginationManager,
        filterManager,
        rowSelectionCustomRenderer,
        stateSelectedKeys: { selectedKeys },
        table: { count },
    } = config;

    const rowSelection = rowSelectionCustomRenderer({
        label: i('common.selected'),
    });

    const { data: caseData } = useOneQuery<CaseOutput>({
        selector: computeGetSelector({ caseId }),
        controller: client.CaseController,
        manual: true,
        fetchOnStart: false,
    });

    const { userPermissions = [] } = caseData || {};

    const [alertStatus, setAlertStatus] = React.useState<CustomStatusMapType>({});

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.AlertController,
                history,
                locale: i,
                onSuccess: (count, message) => {
                    count && countRefetch();
                    message && openMessage('success', message);
                    listRefetch?.();
                },
                onError: handleNotificationError,
            }),
        [countRefetch, history, i, listRefetch],
    );

    const hasPermissions = useSplitPermissions(userPermissions || [], [
        PermissionsEnum.MANAGE_ALERT_UPDATE,
        PermissionsEnum.MANAGE_ALERT_RESTART,
    ]);

    const actionListFunc = useMemo(
        () => computeActionList(i, history, alertStatus, eventList, hasPermissions, currentUserData),
        [alertStatus, eventList, history, currentUserData, i, hasPermissions],
    );

    const {
        customStatus: {
            alert: { list: customStatusList },
        },
    } = useContext(ScopeContext);

    useEffect(() => {
        if (customStatusList.length) {
            const alertStatusMap = customStatusList.reduce<CustomStatusMapType>((acc, curr) => {
                if (curr.stage === AlertStatusStage.IMPORTED) {
                    return acc;
                }
                if (!acc[curr.stage]) {
                    acc[curr.stage] = [];
                }
                acc[curr.stage].push(curr);
                return acc;
            }, {} as CustomStatusMapType);
            setAlertStatus(alertStatusMap);
        }
    }, [customStatusList]);

    usePageTitle(i('page-title.cases.similar-alerts', { number: caseData?.number || 0 }));

    const { page: storagePage, setFilterIsOpen, setAutoRefresh, setOnLoadView } = storagePageManager || { page: null };

    const [stateFilter, setStateFilter] = useState<SimilarStateFilter>(stateDefaultValue);

    const [localAutoRefresh, setLocalAutoRefresh] = useState(storagePage?.autoRefresh || false);
    const [localFilterIsOpen, setLocalFilterIsOpen] = useState(storagePage?.filterIsOpen || false);

    setOnLoadView?.((viewContent) => {
        setLocalAutoRefresh(viewContent?.autoRefresh || false);
    });

    const observableTypesList = useMemo(
        () =>
            dataSource.reduce<string[]>((acc, { observableTypes }) => {
                const tmp: string[] = [...acc, ...Object.keys(observableTypes)];

                return tmp.filter((v, i) => tmp.lastIndexOf(v) === i);
            }, []),
        [dataSource],
    );

    const actionList = actionListFunc(
        dataSource.reduce(
            (acc, { alert }) => (selectedKeys.includes(alert['_id']) ? [...acc, alert] : acc),
            [] as AlertOutput[],
        ),
        (count) => {
            listRefetch?.();
            config.table?.setSelectedKeys?.([]);
            if (count) {
                countRefetch?.();
            }
        },
    );

    const columns = columnsRenderer([
        {
            title: [{ label: i('alert.table.columns.status'), sortField: 'status' }],
            className: 'similar-alerts-list__column-status',
            children: [
                {
                    render: ({ alert: { status = '' } }, { custom: filterCustom }): ReactElement => {
                        const statusConf = getStatus(status);

                        return (
                            <ColorLabel
                                label={i({
                                    id: `alert.data.status.${convertToLocaleId(statusConf?.value || '')}`,
                                    defaultMessage: statusConf?.value,
                                })}
                                color={statusConf?.colour || HTMLColors.DARK_GRAY}
                                className={classList('status-tag')}
                                type="fill"
                                onClick={
                                    status
                                        ? () =>
                                              filterCustom({
                                                  field: 'status',
                                                  values: [status],
                                                  operator: 'any',
                                              })
                                        : undefined
                                }
                            />
                        );
                    },
                },
            ],
        },
        {
            title: [
                { label: i('alert.table.columns.severity'), sortField: 'severity' },
                { label: i('alert.similar-cases.table.title.label'), sortField: 'title' },
            ],
            className: 'similar-alerts-list__column__title',
            children: [
                {
                    title: (
                        <THInput
                            value={stateFilter.title}
                            onChange={(event): void => setStateFilter({ ...stateFilter, title: event.target.value })}
                            placeholder={i('alert.similar-cases.table.title.filter')}
                        />
                    ),
                    className: 'similar-alerts-list__column__title column_preview',
                    render: (
                        { alert },
                        { tags: filterTags, customField: filterCustomField, severity: filterSeverity },
                    ): ReactElement => (
                        <AlertListTitle
                            data={alert}
                            onTagsClick={(tagValue, event): void => {
                                const isAltPressed = event.altKey;
                                filterTags([tagValue], isAltPressed);
                            }}
                            onCustomFieldsClick={(customField): void => filterCustomField(customField)}
                            customFieldsIsOpen={storagePage?.display?.customFields}
                            onPreviewClick={() => {
                                setData(alert);
                                setVisible(true);
                            }}
                            onSeverityClick={filterSeverity}
                        />
                    ),
                },
            ],
        },
        {
            title: [{ label: i('alert.similar-cases.table.created-at'), sortField: '_createdAt' }],
            className: 'similar-alerts-list__column__date',
            children: [
                {
                    className: 'similar-alerts-list__column__date',
                    render: ({ alert: { _createdAt = 0 } }, { date: filterDate }): ReactElement => (
                        <FormatDate date={_createdAt} onClick={(): void => filterDate(_createdAt, '_createdAt')} />
                    ),
                },
            ],
        },
        {
            title: [{ label: i('alert.similar-cases.table.observables.label'), sortField: 'observableCount' }],
            className: 'similar-alerts-list__column__obs-ioc',
            children: [
                {
                    className: 'similar-alerts-list__column__obs-ioc',
                    title: (
                        <THInputNumber
                            defaultValue={0}
                            value={stateFilter.obsPercentage || undefined}
                            onChange={(value): void =>
                                setStateFilter({ ...stateFilter, obsPercentage: parseInt(value + '') || 0 })
                            }
                            placeholder={i('alert.similar-cases.table.observables.filter')}
                        />
                    ),
                    render: ({ similarObservableCount = 0, observableCount = 0 }): ReactElement => (
                        <AlertSimilarCasesStats
                            similar={similarObservableCount}
                            count={observableCount}
                            onClick={(percentage): void => {
                                setStateFilter({
                                    ...stateFilter,
                                    obsPercentage: percentage,
                                });
                            }}
                        />
                    ),
                },
            ],
        },
        {
            title: [{ label: i('alert.similar-cases.table.iocs.label'), sortField: 'iocCount' }],
            className: 'similar-alerts-list__column__obs-ioc',
            children: [
                {
                    className: 'similar-alerts-list__column__obs-ioc',
                    title: (
                        <THInputNumber
                            defaultValue={0}
                            value={stateFilter.iocPercentage || undefined}
                            onChange={(value): void =>
                                setStateFilter({ ...stateFilter, iocPercentage: parseInt(value + '') || 0 })
                            }
                            placeholder={i('alert.similar-cases.table.iocs.filter')}
                        />
                    ),
                    render: ({ similarIocCount = 0, iocCount = 0 }): ReactElement => (
                        <AlertSimilarCasesStats
                            similar={similarIocCount}
                            count={iocCount}
                            onClick={(percentage): void => {
                                setStateFilter({
                                    ...stateFilter,
                                    iocPercentage: percentage,
                                });
                            }}
                        />
                    ),
                },
            ],
        },
        {
            title: i('alert.similar-cases.table.matches.label'),
            className: 'similar-alerts-list__column__matches',
            children: [
                {
                    className: 'similar-alerts-list__column__matches',
                    title: (
                        <THTagSelector.Autocomplete
                            taxonomies={false}
                            value={stateFilter.dataTypes}
                            placeholder={i('alert.similar-cases.table.matches.filter')}
                            dropdownRender={(menu): ReactElement => (
                                <div>
                                    <THButton
                                        icon={<ICONS.CheckIcon />}
                                        block
                                        onClick={(): void =>
                                            setStateFilter({ ...stateFilter, dataTypes: observableTypesList })
                                        }
                                        size="small"
                                    >
                                        {i('alert.similar-cases.table.matches.select-all')}
                                    </THButton>
                                    <THButton
                                        icon={<ICONS.CloseIcon />}
                                        block
                                        onClick={(): void => setStateFilter({ ...stateFilter, dataTypes: [] })}
                                        size="small"
                                    >
                                        {i('alert.similar-cases.table.matches.unselect-all')}
                                    </THButton>
                                    {menu}
                                </div>
                            )}
                            tagRender={({ label }) => <THTag value={label} full textBlack />}
                            options={observableTypesList.map((observableType) => ({
                                label: observableType,
                                value: observableType,
                            }))}
                            onChange={(value): void => setStateFilter({ ...stateFilter, dataTypes: value })}
                            className="similar-alerts-list__column__matches-select"
                        />
                    ),
                    render: ({ observableTypes }): ReactElement => (
                        <span>
                            {Object.entries(observableTypes).map(([key, value]) => (
                                <div key={key}>{`${key} (${value})`}</div>
                            ))}
                        </span>
                    ),
                },
            ],
        },
        {
            className: 'similar-alerts-list__column__actions',
            children: [
                {
                    className: 'similar-cases-list__column__actions',
                    title: (
                        <THButton
                            icon={<ICONS.CloseIcon />}
                            type="link"
                            onClick={() => setStateFilter(stateDefaultValue)}
                        >
                            {i('alert.similar-cases.table.actions.clear')}
                        </THButton>
                    ),
                    render: ({ alert }): ReactElement | null =>
                        hasPermissions.updateAlert ? (
                            <ActionsList
                                actionList={{
                                    merge: {
                                        label: i('alert.similar-cases.table.actions.merge'),
                                        query: () =>
                                            client.AlertController.mergeIntoCase({ alertId: alert._id, caseId })
                                                .then((): void => {
                                                    history.push(RouteBuilder.compute('cases.details', { caseId }));
                                                })
                                                .catch(handleNotificationError),
                                        icon: <ICONS.MergeIcon />,
                                    },
                                    ...actionListFunc([alert], (count) => {
                                        listRefetch();
                                        if (count) {
                                            countRefetch();
                                        }
                                    }),
                                }}
                                isIcon
                                scope="single"
                            />
                        ) : null,
                },
            ],
        },
    ]);

    const filteredDataSource = useMemo(
        () =>
            dataSource
                .sort(
                    (
                        {
                            alert: { title: titleA = '', _createdAt: _createdAtA = 0, severity: severityA = 0 },
                            observableCount: observableCountA = 0,
                            iocCount: iocCountA = 0,
                            similarIocCount: similarIocCountA = 0,
                            similarObservableCount: similarObservableCountA = 0,
                        },
                        {
                            alert: { title: titleB = '', _createdAt: _createdAtB = 0, severity: severityB = 0 },
                            observableCount: observableCountB = 0,
                            iocCount: iocCountB = 0,
                            similarIocCount: similarIocCountB = 0,
                            similarObservableCount: similarObservableCountB = 0,
                        },
                    ): number => {
                        const iocPercentage =
                            Math.floor((similarIocCountB / (iocCountB || 1)) * 100) -
                            Math.floor((similarIocCountA / (iocCountA || 1)) * 100);

                        const obsPercentage =
                            Math.floor((similarObservableCountB / (observableCountB || 1)) * 100) -
                            Math.floor((similarObservableCountA / (observableCountA || 1)) * 100);

                        let sort = 0;
                        if ((sort = getFieldSort('title'))) {
                            return titleA.localeCompare(titleB) * -sort;
                        }
                        if ((sort = getFieldSort('_createdAt'))) {
                            return (_createdAtB - _createdAtA) * -sort;
                        }
                        if ((sort = getFieldSort('observableCount'))) {
                            return obsPercentage * -sort;
                        }
                        if ((sort = getFieldSort('iocCount'))) {
                            return iocPercentage * -sort;
                        }
                        if ((sort = getFieldSort('severity'))) {
                            return (severityB - severityA) * -sort;
                        }
                        return titleA.localeCompare(titleB) * -1;
                    },
                )
                .filter(
                    ({
                        alert: { title = '' },
                        iocCount = 1,
                        similarIocCount,
                        observableCount = 1,
                        similarObservableCount,
                        observableTypes,
                    }) => {
                        const checkIoc =
                            Math.floor((similarIocCount / (iocCount || 1)) * 100) >= stateFilter.iocPercentage;

                        const checkObs =
                            Math.floor((similarObservableCount / (observableCount || 1)) * 100) >=
                            stateFilter.obsPercentage;

                        const hasDatatype = stateFilter.dataTypes.length
                            ? Object.keys(observableTypes).some((value) => stateFilter.dataTypes.includes(value))
                            : true;

                        return (
                            checkIoc &&
                            checkObs &&
                            hasDatatype &&
                            title.toLowerCase().includes(stateFilter.title.toLowerCase())
                        );
                    },
                )
                .splice(
                    (paginationManager?.page || 0) * (paginationManager?.itemsPerPage || 0),
                    paginationManager?.itemsPerPage || 0,
                ),
        [dataSource, stateFilter, getFieldSort, paginationManager],
    );

    useEffect(() => {
        setAutoRefresh?.(localAutoRefresh);
    }, [localAutoRefresh]); // eslint-disable-line

    const setLocalFilters = (localFilters: boolean): void => {
        setLocalFilterIsOpen(localFilters);
        setFilterIsOpen?.(localFilters);
        filterManager.setEditFilters(localFilters);
    };

    useEffect(() => {
        setLocalFilterIsOpen(filterManager.editFilters);
    }, [filterManager.editFilters]); // eslint-disable-line

    useDrawer.watch(dataSource, () => {
        if (data) {
            setData(dataSource.find(({ alert: { _id } }) => _id === data._id)?.alert);
        }
    });

    return (
        <THLayout>
            <>
                <THList>
                    <THList.Header>
                        <THList.Slot name="MANAGE">
                            {storagePageManager && storageViewManager ? (
                                <ManageViewList
                                    pageManager={storagePageManager}
                                    viewManager={storageViewManager}
                                    beforeCreate={(content: any): any => {
                                        filterManager.applyFilters();
                                        return { ...content, filters: filterManager.tempFilters };
                                    }}
                                />
                            ) : null}
                            <ActionList actions={actionList} scope="list" hidden={!selectedKeys?.length} />
                        </THList.Slot>
                        <THList.Slot name="NAVIGATION">
                            {!storagePage?.autoRefresh && (
                                <Badge dot={needRefresh}>
                                    <THTooltip
                                        title={needRefresh ? i('common.refresh') : i('common.update-list')}
                                        placement="bottom"
                                    >
                                        <THButton
                                            size="small"
                                            type={needRefresh ? 'primary' : 'default'}
                                            shape="circle"
                                            onClick={() => {
                                                listRefetch();
                                                countRefetch();
                                                setNeedRefresh(false);
                                            }}
                                        >
                                            <ICONS.SyncOutlined />
                                        </THButton>
                                    </THTooltip>
                                </Badge>
                            )}
                            <ManageSwitch
                                label={i('common.auto-refresh')}
                                value={localAutoRefresh}
                                onChange={(autoRefresh): void => setLocalAutoRefresh(autoRefresh)}
                                icon={<ICONS.SyncOutlined />}
                            />

                            <ManageSwitch
                                label={i('common.filters')}
                                value={localFilterIsOpen}
                                onChange={(editFilters): void => setLocalFilters(editFilters)}
                                icon={<ICONS.FilterOutlined />}
                            />
                        </THList.Slot>
                        <THList.Slot name="CONTENT">
                            {(filterManager.editFilters || !!filterManager.tempFilters.length) && (
                                <THFilter filterConfig={filterManager} describeField={describe?.[ObjectType.ALERT]}>
                                    <Footer filterConfig={filterManager} />
                                </THFilter>
                            )}
                        </THList.Slot>
                    </THList.Header>

                    <THList.Content
                        dataSource={filteredDataSource.map((similar) => ({ ...similar, _thid: similar.alert._id }))}
                        columns={columns}
                        rowKey="_thid"
                        rowSelection={hasPermissions.updateAlert ? rowSelection : undefined}
                        locale={{
                            emptyText: (
                                <DisplayEmptyData spin={status === 'loading'} label={i('case.similar-alerts.none')} />
                            ),
                        }}
                    />

                    <THList.Footer>
                        <THList.Slot name="NAVIGATION">
                            <PaginationBrowse count={count} paginationConfig={paginationManager} />
                            <PaginationSize paginationConfig={paginationManager} />
                        </THList.Slot>
                    </THList.Footer>
                </THList>

                {data && visible && <AlertDrawer data={data} visible={visible} afterVisible={setVisible} />}
            </>
        </THLayout>
    );
};

const MenuIcon = (): ReactElement => {
    const { caseId } = useParams<{ caseId: string }>();
    const name = 'get-count-case-similar-alerts';

    const queryParams = {
        selector: {
            head: [
                {
                    name: 'getCase',
                    idOrName: caseId,
                },
                {
                    name: 'similarAlertCount',
                },
            ],
            name,
        },
    };

    const query = useQuery([name, caseId], () => client.AlertController.custom<number>(queryParams as QueryParams), {
        refetchInterval: false,
        refetchOnWindowFocus: false,
    });

    const { data: count = 0, refetch } = query;

    useEffect(() => {
        refetch();
    }, [refetch]);

    return count ? (
        <IconCount count={count}>
            <ICONS.AlertIcon />
        </IconCount>
    ) : (
        <ICONS.AlertIcon />
    );
};

export const Route: RouteType = {
    menu: {
        label: 'case.layout.nav-bar.similar-alerts',
        icon: <ICONS.AlertIcon />,
        shortcut: {
            enable: true,
            render: <MenuIcon />,
        },
    },
    path: RouteBuilder.compute('cases.similar-alerts'),
    content: <SimilarAlertsList />,
    Context: ListProvider,
    space: ['user'],
    breadcrumb: { label: 'breadcrumb.case.similar-alerts' },
};
